import Pusher from 'pusher-js/with-encryption';
import { baseURL } from 'api/forethoughtFetch';
import { getOrganizationId } from 'components/salesforce-messaging-one-chat/dataProvider';
import { getStore } from 'storeRef';
import { selectApiKey } from 'reducers/config';

interface ChannelAuthorizationData {
  auth: string;
  channel_data?: string;
  shared_secret?: string;
}

// Global Pusher instance shared across the application
let globalPusher: Pusher | null = null;

const createPusherAuthHandler = (endpoint: string) => {
  return async (
    params: { channelName: string; socketId: string },
    callback: (
      error: Error | null,
      authData: ChannelAuthorizationData | null,
    ) => void,
  ) => {
    try {
      const store = getStore();
      const apiKey = selectApiKey(store.getState());

      const headers: Record<string, string> = {};

      // Add Authorization header with API key
      headers['Authorization'] = `Bearer ${apiKey}`;
      headers['X-ORG-ID'] = getOrganizationId();

      const response = await fetch(endpoint, {
        body: JSON.stringify({
          channel_name: params.channelName,
          socket_id: params.socketId,
        }),
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Pusher authorization failed: ${response.status}`);
      }

      const authData = (await response.json()) as ChannelAuthorizationData;
      callback(null, authData);
    } catch (error) {
      console.error('Error authorizing Pusher channel:', error);
      callback(error as Error, null);
    }
  };
};

/**
 * Gets or creates the global Pusher instance
 */
export const getOrCreatePusher = (): Pusher => {
  if (!globalPusher) {
    const PUSHER_KEY =
      process.env.REACT_APP_PUSHER_KEY || '7d93496a6cb894633b32';
    const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || 'us3';
    const endpoint = `${baseURL}/pusher/solve/auth`;

    globalPusher = new Pusher(PUSHER_KEY, {
      channelAuthorization: {
        customHandler: createPusherAuthHandler(endpoint),
        endpoint,
        transport: 'ajax',
      },
      cluster: PUSHER_CLUSTER,
      forceTLS: true,
    });
  }
  return globalPusher;
};
